import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableCallbacks, ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { FloatingSelectionToolbar } from '../../../components/table/FloatingSelectionToolbar';
import { SelfBillKind, SupplierSelfBillStatus } from 'src/types/enums';
import { getSelfBills, generateSelfBillings, type SelfBillListEntry } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDateTime } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { Input } from '../../../components/input/Input';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { getDisplayError } from '@utils/get-display-error';
import { PageHeading } from 'components/PageHeading';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { captureException } from '@sentry/react';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';
import { Pill, PillColor } from 'components/pill/Pill';
import { Button } from 'components/button/Button';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { ExportSelfBillsButton } from './ExportSelfBillsButton';
import { useSelfBillBulkActions } from './bulkActions/useSelfBillBulkActions';
import { SelfBillBulkActionsMenu } from './bulkActions/SelfBillBulkActionsMenu';
import { BulkApproveConfirmDialog } from './bulkActions/BulkApproveConfirmDialog';
import { BulkConvertToPurchasesConfirmDialog } from './bulkActions/BulkConvertToPurchasesConfirmDialog';
import { PeppolStatusPill } from '../../peppol/components/PeppolStatusPill';
import { SELF_BILL_KIND_CONFIG } from './self-bill-kind-config';

type SelfBillSortBy = 'createdAt' | 'dueDate' | 'status' | 'entryNumber' | 'invoiceNumber';

const SELF_BILL_HEADERS: ITableHeader[] = [
  { id: 'entryNumber', name: 'Ref', sortable: true },
  { id: 'invoiceNumber', name: 'Nummer', sortable: true },
  { id: 'supplier', name: 'Vervoerder' },
  { id: 'status', name: 'Status', sortable: true },
  { id: 'peppolStatus', name: 'Peppol' },
  { id: 'createdAt', name: 'Datum', sortable: true },
  { id: 'dueDate', name: 'Vervaldatum', sortable: true },
];

type SelfBillRow = SelfBillListEntry;

const STATUS_OPTIONS = [
  { key: 'ALL', name: 'Alle statussen' },
  { key: SupplierSelfBillStatus.Draft, name: 'Concept' },
  { key: SupplierSelfBillStatus.Sent, name: 'Verzonden' },
  { key: SupplierSelfBillStatus.Failed, name: 'Mislukt' },
];

function getStatusColor(status: SelfBillRow['status']): PillColor {
  switch (status) {
    case SupplierSelfBillStatus.Sent:
      return 'positive';
    case SupplierSelfBillStatus.Failed:
      return 'negative';
    case SupplierSelfBillStatus.Draft:
    default:
      return 'primary';
  }
}

function getStatusLabel(status: SelfBillRow['status']): string {
  switch (status) {
    case SupplierSelfBillStatus.Sent:
      return 'Verzonden';
    case SupplierSelfBillStatus.Failed:
      return 'Mislukt';
    case SupplierSelfBillStatus.Draft:
    default:
      return 'Concept';
  }
}

export interface ISelfBillingsPageProps {
  kind: SelfBillKind;
}

const SelfBillingsPage: React.FC<ISelfBillingsPageProps> = ({ kind }) => {
  const config = SELF_BILL_KIND_CONFIG[kind];
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams['status'] ?? 'ALL');
  const [selections, setSelections] = useState<SelfBillRow[]>([]);
  const [tableCallbacks, setTableCallbacks] = useState<ITableCallbacks | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [sortBy, setSortBy] = useState<SelfBillSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);

  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getSelfBills(
        variables.cursor,
        variables.take,
        {
          kind,
          search: searchValue,
          status: statusFilter === 'ALL' ? null : (statusFilter as SupplierSelfBillStatus),
        },
        sortBy,
        sortOrder,
      );
    },
    [kind, searchValue, statusFilter, sortBy, sortOrder],
  );

  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
    refreshToken,
  });

  const bulk = useSelfBillBulkActions({
    config,
    selections,
    onSettled: () => {
      tableCallbacks?.resetSelections();
      setRefreshToken((v) => v + 1);
    },
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue, setSearchParams],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setSearchParams({ status: value });
    },
    [setSearchParams],
  );

  const statusSelectedItem = useMemo(
    () => STATUS_OPTIONS.find((o) => o.key === statusFilter) ?? STATUS_OPTIONS[0],
    [statusFilter],
  );

  const title = config.title;
  const headers = config.hasPeppol
    ? SELF_BILL_HEADERS
    : SELF_BILL_HEADERS.filter((header) => header.id !== 'peppolStatus');

  const activeFilterCount = [searchValue.trim(), statusFilter !== 'ALL'].filter(Boolean).length;

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <>
                {config.canExportToExact && <ExportSelfBillsButton />}
                <ConfirmDialog
                  triggerText="Genereer concepten"
                  title="Concepten genereren?"
                  submitText="Genereer concepten"
                  description="Maakt ritoverzichten en self billings aan voor uitgevoerde orders met goedgekeurde documenten. Je keurt ze daarna zelf goed."
                  onSubmit={async () => {
                    try {
                      await generateSelfBillings();
                      toast.success('Concepten gegenereerd');
                      setRefreshToken((v) => v + 1);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon concepten niet genereren: ${getDisplayError(err)}`);
                    }
                  }}
                />
              </>
            }
          />
        }
        panel
        filters={
          <FilterPanel
            flush
            activeCount={activeFilterCount}
            actions={
              <div className="flex items-center gap-2">
                <Button
                  size={8}
                  color="default"
                  isDisabled={!page.data.length || !tableCallbacks}
                  onTrigger={() => {
                    if (selections.length === page.data.length) {
                      tableCallbacks?.resetSelections();
                    } else {
                      tableCallbacks?.selectAll();
                    }
                  }}
                >
                  {selections.length === page.data.length && page.data.length > 0
                    ? 'Deselecteer alles'
                    : 'Selecteer alles'}
                </Button>
                <Button
                  size={8}
                  color="secondary"
                  iconLeft={<XIcon className="button-icon" />}
                  onTrigger={() => {
                    setSearchValue('');
                    handleStatusChange('ALL');
                  }}
                >
                  Wis alle filters
                </Button>
              </div>
            }
          >
            <FilterField label="Zoek" span={2}>
              <Input
                type="text"
                placeholder={`Zoek een ${config.documentName.toLowerCase()}...`}
                value={searchValue}
                onChange={setSearchValue}
                iconLeft={<MagnifyingGlassIcon className="input-icon" />}
              />
            </FilterField>

            <FilterField label="Status">
              <SimpleSelect
                items={STATUS_OPTIONS}
                selectedItem={statusSelectedItem}
                onSelect={(item) => handleStatusChange(item?.key ?? 'ALL')}
              />
            </FilterField>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table<SelfBillRow>
          className="h-full"
          idKey="id"
          isMultiSelect
          onSelect={setSelections}
          registerCallbacks={setTableCallbacks}
          headers={headers}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as SelfBillSortBy | undefined);
            setSortOrder(order);
            page.reset();
          }}
          mapData={(selfBill) => {
            const invoiceNumber = selfBill.linkedPurchase?.invoiceNumber ?? selfBill.invoiceNumber;
            return [
              <Link to={`${selfBill.id}`} className="link-text">
                {selfBill.entryNumber}
              </Link>,
              selfBill.linkedPurchase && invoiceNumber ? (
                <Link to={`/internal/finance/purchases/${selfBill.linkedPurchase.id}`} className="link-text">
                  {invoiceNumber}
                </Link>
              ) : (
                (invoiceNumber ?? '-')
              ),
              selfBill.supplier.name,
              <Pill color={getStatusColor(selfBill.status)} size="sm">
                {getStatusLabel(selfBill.status)}
              </Pill>,
              ...(config.hasPeppol
                ? [selfBill.peppolStatus === 'NotSent' ? '-' : <PeppolStatusPill status={selfBill.peppolStatus} />]
                : []),
              formatDateTime(selfBill.createdAt),
              formatDateTime(selfBill.dueDate),
            ];
          }}
        />
      </ListPage>

      <FloatingSelectionToolbar
        selectionCount={selections.length}
        isDisabled={bulk.isBusy}
        onClear={() => tableCallbacks?.resetSelections()}
        onOpenActions={bulk.open}
        quickActions={
          <Button
            color="primary"
            isDisabled={!bulk.hasDraft || bulk.isBusy}
            isLoading={bulk.isBusy}
            onTrigger={bulk.approveDialog.open}
          >
            Goedkeuren...
          </Button>
        }
      />

      <SelfBillBulkActionsMenu config={config} selectionCount={selections.length} bulk={bulk} />

      <BulkApproveConfirmDialog
        config={config}
        isOpen={bulk.approveDialog.isOpen}
        onClose={bulk.approveDialog.close}
        draftCount={bulk.draftCount}
        isBusy={bulk.isBusy}
        onApprove={bulk.handleApprove}
      />

      <BulkConvertToPurchasesConfirmDialog
        config={config}
        isOpen={bulk.convertToPurchasesDialog.isOpen}
        onClose={bulk.convertToPurchasesDialog.close}
        sentCount={bulk.sentCount}
        isBusy={bulk.isBusy}
        onConvert={bulk.handleConvertToPurchases}
      />
    </>
  );
};

export default SelfBillingsPage;
